/* eslint-disable */
import React, { useState, useContext, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Formik } from 'formik';
import { useMutation, useQuery } from 'react-fetching-library';
import { makeStyles } from '@material-ui/core';
import ContentWrapper from 'components/Wrappers/ContentWrapper';
import RoundedButton from 'components/Shared/RoundedButton';
import showServerError from 'helpers/showError';
import { ALL_ROUTES } from 'config/routes';
import OrderForm from 'components/Orders/OrderForm';
import { AppRouteContext } from 'contexts';
import useMetadataUpdate from 'hooks/useMetadataUpdate';
import CreateOrderDialog from 'components/Dialogs/CreateOrderDialog';
import BackButton from 'components/Shared/BackButton';
import { useDispatch, useSelector } from 'react-redux';
import EmptyPageLoader from '../Shared/EmptyPageLoader';
import {
  enqueueSnackbar,
  SNACKBAR_VARIANTS,
} from '../../redux_store/reducer/reducers/notificationsReducer';
import { getTransitionWithVariable } from '../../redux_store/reducer/reducers/i18nReducer';

const useStyles = makeStyles((theme) => ({
  contentContainer: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    position: 'relative',
    width: '100%',
  },
  formCard: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    background: '#ffffff',
    borderRadius: 16,
    boxShadow: '0 4px 24px rgba(0, 0, 0, 0.08)',
    overflow: 'hidden',
    marginBottom: theme.spacing(10),
    margin: theme.spacing(3, 0),
    [theme.breakpoints.down('sm')]: {
      margin: theme.spacing(2, 0),
      borderRadius: 12,
    },
  },
  scrollableContent: {
    flex: 1,
    overflow: 'auto',
    background: 'linear-gradient(135deg, #fafafa 0%, #ffffff 100%)',
  },
  formContent: {
    width: '100%',
  },
  actionsContainer: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    width: '100%',
    padding: theme.spacing(3, 6, 4),
    background: '#ffffff',
    borderTop: '1px solid rgba(0, 0, 0, 0.12)',
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'center',
    gap: theme.spacing(2),
    boxShadow: '0 -4px 16px rgba(0, 0, 0, 0.08)',
    [theme.breakpoints.down('md')]: {
      padding: theme.spacing(2, 4, 3),
    },
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(2, 2, 3),
      gap: theme.spacing(1),
    },
  },
  actionButton: {
    textTransform: 'uppercase',
    borderRadius: 8,
    padding: theme.spacing(1.5, 2),
    letterSpacing: 1.5,
    fontWeight: 600,
    fontSize: 14,
    minWidth: 100,
    height: 40,
    [theme.breakpoints.down('sm')]: {
      minWidth: 100,
      padding: theme.spacing(1, 2),
      fontSize: 13,
    },
  },
  backButton: {
    marginRight: 'auto',
  },
}));

const OrderCreate = () => {
  const classes = useStyles();
  const { accountUuid, patientUuid, reorderUuid = 'newOrder' } = useParams();
  const [initialValues, setInitialValues] = useState();
  const [backendErrors, setBackendErrors] = useState({});
  const [isOpenConfirmDialog, setOpenConfirmDialog] = useState(false);
  const dispatch = useDispatch();
  const { setAppRoute } = useContext(AppRouteContext);
  const { user } = useSelector((state) => state.user);
  const [isSuccess, setIsSuccess] = useState(false);
  const [isFailed, setIsFailed] = useState(false);
  const { metadata: { currentAccount } } = useSelector((state) => state.user);
  const {
    en: {
      titles,
      errors,
      notifications,
      btn,
    },
  } = useSelector((state) => state.i18n);

  const { payload: metadata, error: metadataError } = useMetadataUpdate(
    useQuery,
    [
      {
        method: 'GET',
        endpoint: `/accounts/${accountUuid || currentAccount.uuid}/patients/${patientUuid}/create-order-metadata`,
      },
    ],
  );

  const { query, loading: loadingInitialData } = useMetadataUpdate(useQuery, [
    {
      method: 'GET',
      endpoint: `/accounts/${accountUuid || currentAccount.uuid}/orders/${reorderUuid}`,
    },
    false,
  ]);

  const orderCreateAction = (values) => ({
    method: 'PUT',
    endpoint: `/accounts/${accountUuid || currentAccount.uuid}/patients/${patientUuid}/orders`,
    body: values,
  });
  const { loading, mutate } = useMetadataUpdate(useMutation, [
    orderCreateAction,
  ]);

  // set selected items when all items have been loaded
  useEffect(() => {
    if (reorderUuid !== 'newOrder') {
      query().then(({ error, payload }) => {
        if (!error) {
          const newInitData = {
            ...payload,
            status: 'pending',
          };

          if (payload.category === 'slit') {
            newInitData.slitMaintenance = true;
            newInitData.slitMaintenanceQuantity = 3;
            newInitData.allergySymptoms = [];
            newInitData.symptomsExposure = [];
            newInitData.symptomsTimeOfYear = [];
            newInitData.symptomsTimeInDay = '';
            newInitData.symptomsMostlyOccur = '';
            newInitData.examinationReveals = [];
            newInitData.allergyDiagnosis = [];
          }

          if (payload.category === 'scit') {
            newInitData.scitType = 'twelveDosePerUnitSCITMaintenance';
          }

          setInitialValues(newInitData);
        }
      });
    }
  }, []);

  useEffect(() => {
    if (metadata && (reorderUuid === 'newOrder')) {
      const { providers, addresses } = metadata;
      setInitialValues({
        providerUserUUID: providers.length ? providers[0].uuid : '',
        category: '',
        scitType: '',
        scitAllergens: [],
        slitLowDose: false,
        slitLowDoseQuantity: 0,
        slitMaintenance: false,
        slitMaintenanceQuantity: 0,
        address: addresses.length ? addresses[0] : {},
        comments: '',
        allergySymptoms: [],
        symptomsExposure: [],
        symptomsTimeOfYear: [],
        symptomsTimeInDay: '',
        symptomsMostlyOccur: '',
        examinationReveals: [],
        allergyDiagnosis: [],
      });
    }
  }, [metadata]);

  const handleOpenConfirmDialog = () => {
    setOpenConfirmDialog(true);
  };

  const handleCloseConfirmDialog = () => {
    setOpenConfirmDialog(false);
  };

  const createOrder = async (values, { setSubmitting, setFieldValue }) => {
    handleCloseConfirmDialog();
    setBackendErrors({});
    const {
      slitLowDose,
      slitLowDoseQuantity,
      slitMaintenance,
      slitMaintenanceQuantity,
    } = values;

    const formattedValues = {
      ...values,
      slitLowDoseQuantity:
        slitLowDose && slitLowDoseQuantity ? slitLowDoseQuantity : 0,
      slitMaintenanceQuantity:
        slitMaintenance && slitMaintenanceQuantity
          ? slitMaintenanceQuantity
          : 0,
    };
    delete formattedValues.slitLowDose; // internal usage
    delete formattedValues.slitMaintenance; // internal usage

    const { payload, error, status } = await mutate(formattedValues);
    if (error) {
      setIsFailed(true);
      setTimeout(() => {
        setIsFailed(false);
      }, 2000);
      if (!slitLowDose) {
        setFieldValue('slitLowDoseQuantity', 0);
      }
      if (!slitMaintenance) {
        setFieldValue('slitMaintenanceQuantity', 0);
      }

      const options = payload && {
        correlationUUID: payload.correlationUUID,
        userUUID: user.uuid,
      };
      setSubmitting(false);
      switch (status) {
        case 422: {
          if (payload.validationErrors.status === 'The specified condition was not met for \'status\'.') {
            showServerError(dispatch, errors.specifiedCondition);
            break;
          }

          showServerError(dispatch, errors.validationError);
          setBackendErrors(payload.validationErrors);
          break;
        }
        default: {
          showServerError(dispatch, '', options);
          break;
        }
      }
    } else if (payload) {
      setIsSuccess(true);
      setTimeout(() => {
        setIsSuccess(false);
      }, 2000);
      dispatch(
        enqueueSnackbar({
          message: getTransitionWithVariable(notifications.orderCreated, {
            name: metadata.patientInfo.name,
          }),
          options: {
            variant: SNACKBAR_VARIANTS.success,
          },
        }),
      );
      setAppRoute({
          path: ALL_ROUTES.order.list.path,
          title: ALL_ROUTES.order.list.additional.title,
          params: {
            currentAccountUuid: currentAccount.uuid,
          },
          clearBreadcrumbs: true,
        },
        {
          addDelay: true,
        },
      );
    }
  };

  if (!metadata || metadataError || loadingInitialData || !initialValues) {
    return <EmptyPageLoader />;
  }

  return (
    <Formik
      initialValues={initialValues}
      validateOnBlur={false}
      onSubmit={createOrder}
    >
      {({ isValid, handleSubmit, dirty, isSubmitting, values }) => (
        <ContentWrapper
          titleText={titles.createOrder}
          containerStyle={{ overflow: 'visible' }}
        >
          <div className={classes.contentContainer}>
            <div className={classes.formCard}>
              <div className={classes.scrollableContent}>
                <div className={classes.formContent}>
                  <OrderForm
                    values={values}
                    backendErrors={backendErrors}
                    metadata={metadata}
                  />
                </div>
              </div>
            </div>
            <div className={classes.actionsContainer}>
              <BackButton className={classes.backButton} />
              <RoundedButton
                variant='contained'
                color='primary'
                disabled={!isValid || isSubmitting || (reorderUuid === 'newOrder' && !dirty)}
                onClick={handleOpenConfirmDialog}
                isLoading={loading}
                isSuccess={isSuccess}
                isFailed={isFailed}
                className={classes.actionButton}
              >
                {btn.save}
              </RoundedButton>
            </div>
          </div>

          <CreateOrderDialog
            open={isOpenConfirmDialog}
            onClose={handleCloseConfirmDialog}
            onSubmit={handleSubmit}
          />
        </ContentWrapper>
      )}
    </Formik>
  );
};

export default OrderCreate;
